import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { getTotalUnreadNotifications } from "src/services/NotificationAnnouncementsAPIs";
import NotificationService from "src/services/NotificationService";

interface NotificationContextProps {
  count: number;
  refreshCount: () => Promise<void>;
  /** Aligns badge with unread count derived from `fetchAllNotifications` (same source as the header popup). */
  syncUnreadWithFetchedList: (unreadTotal: number) => void;
  pushEnabled: boolean;
  pushPermission: NotificationPermission;
  enablePushNotifications: (userId: number) => Promise<void>;
  disablePushNotifications: (userId: number) => Promise<void>;
  requestPushPermission: () => Promise<NotificationPermission>;
}

const NotificationContext = createContext<NotificationContextProps>({
  count: 0,
  refreshCount: async () => { },
  syncUnreadWithFetchedList: () => { },
  pushEnabled: false,
  pushPermission: "default",
  enablePushNotifications: async () => { },
  disablePushNotifications: async () => { },
  requestPushPermission: async () => "default",
});

const normalizeUnreadCount = (value: unknown): number => {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
    return 0;
  }
  return Math.min(Math.floor(value), 1_000_000);
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Do not hydrate from localStorage: a stale total produced a misleading badge (e.g. "99+")
  // while the notifications list from the API was empty. Count comes from refreshCount / list sync.
  const [count, setCount] = useState<number>(0);
  const [pushEnabled, setPushEnabled] = useState<boolean>(() => {
    return localStorage.getItem("push_notifications_enabled") === "true";
  });
  const [pushPermission, setPushPermission] = useState<NotificationPermission>("default");
  /** Bumps when `syncUnreadWithFetchedList` runs so in-flight `refreshCount` does not overwrite list-backed totals. */
  const countMutationGenerationRef = useRef(0);

  const syncUnreadWithFetchedList = useCallback((unreadTotal: number) => {
    countMutationGenerationRef.current += 1;
    const safe = normalizeUnreadCount(unreadTotal);
    setCount(safe);
    localStorage.setItem("total_unread_count", String(safe));
  }, []);

  const refreshCount = useCallback(async () => {
    const generationAtStart = countMutationGenerationRef.current;
    try {
      const latestCount = await getTotalUnreadNotifications();
      if (countMutationGenerationRef.current !== generationAtStart) {
        return;
      }
      const safe = normalizeUnreadCount(latestCount);
      setCount(safe);
      localStorage.setItem("total_unread_count", String(safe));
    } catch (error) {
      console.error("Failed to refresh unread count", error);
      if (countMutationGenerationRef.current !== generationAtStart) {
        return;
      }
      setCount(0);
      localStorage.removeItem("total_unread_count");
    }
  }, []);

  const requestPushPermission = async () => {
    const permission = await NotificationService.requestPermission();
    setPushPermission(permission);
    return permission;
  };

  const enablePushNotifications = async (_userId?: number) => {
    try {
      const permission = await requestPushPermission();
      if (permission === "granted") {
        await NotificationService.registerServiceWorker();
        const subscription = await NotificationService.subscribeUserToPush();
        if (subscription) {
          setPushEnabled(true);
          localStorage.setItem("push_notifications_enabled", "true");
        }
      }
    } catch (error) {
      console.error("Error enabling push notifications:", error);
      setPushEnabled(false);
      localStorage.setItem("push_notifications_enabled", "false");
    }
  };

  const disablePushNotifications = async (_userId?: number) => {
    try {
      const success = await NotificationService.unsubscribeFromPush();
      if (success) {
        setPushEnabled(false);
        localStorage.setItem("push_notifications_enabled", "false");
      }
    } catch (error) {
      console.error("Error disabling push notifications:", error);
    }
  };

  useEffect(() => {
    const initializeNotifications = async () => {
      try {
        // Always register service worker on startup
        //console.log('Registering service worker...');
        const registration = await NotificationService.registerServiceWorker();
        //console.log('Service worker registration result:', !!registration);

        // Check permission and update state
        const permission = await NotificationService.checkPermission();
       // console.log('Current notification permission:', permission);
        setPushPermission(permission);

        // If push was previously enabled and permission is granted, resubscribe
        if (pushEnabled && permission === "granted") {
          const subscription = await NotificationService.subscribeUserToPush();
          setPushEnabled(!!subscription);
        }

        // Refresh notification count
        await refreshCount();
      } catch (error) {
        console.error("Error initializing notifications:", error);
        setPushEnabled(false);
        localStorage.setItem("push_notifications_enabled", "false");
      }
    };

    initializeNotifications();
  }, [refreshCount]);

  return (
    <NotificationContext.Provider
      value={{
        count,
        refreshCount,
        syncUnreadWithFetchedList,
        pushEnabled,
        pushPermission,
        enablePushNotifications,
        disablePushNotifications,
        requestPushPermission
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => useContext(NotificationContext);
